import type { FC } from 'react';
import { Link, useBreakpointValue } from '@chakra-ui/react';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { ShopperProductsTypes } from 'commerce-sdk-isomorphic';
import { Text } from '../../ui-components/typography';
import { classNames } from '../../utils/tailwind/classNames';
import { GetIcon } from '../icons';
import styles from './CategoryButtonTiles.module.scss';

export type Category = ShopperProductsTypes.Category & {
    c_productInfoTile?: string[];
    c_template?: string;
    c_href: string;
    c_slotBannerImage: string;
};

export type CategoryButtonTilesProps = {
    items: Category[];
    dataTestId?: string;
};

/**
 * This component is used to render a list of categories as buttons.
 *
 * @param calloutMsg - The message to display above the buttons.
 * @param items - The list of categories to render.
 * @returns A VStack with the callout message and the buttons.
 */
const CategoryButtonTiles: FC<CategoryButtonTilesProps> = ({ items, dataTestId }) => {
    const showNavigation = useBreakpointValue({ base: false, sm: false, md: true });

    return (
        <div
            className={classNames(
                styles.categoryButtonTiles,
                'w-full h-auto overflow-hidden relative',
            )}
            data-testid={dataTestId}
        >
            <Swiper
                data-testid={`${dataTestId}-category-button-tiles`}
                className="w-full"
                modules={[Navigation]}
                slidesPerView="auto"
                navigation={
                    showNavigation
                        ? {
                              nextEl: '.swiper-button-next',
                              prevEl: '.swiper-button-prev',
                          }
                        : false
                }
                wrapperClass="gap-3"
            >
                {items.map((item: Category) => (
                    <SwiperSlide
                        data-testid={`${dataTestId}-category-button-tile-${item.id}`}
                        key={item.id}
                    >
                        <Link
                            className="group flex h-full w-full items-center rounded-lg border border-slt-grey-light bg-slt-grey-light p-2 transition-all hover:border hover:border-slt-pepper-300"
                            href={item.c_href}
                            _hover={{ textDecoration: 'none' }}
                        >
                            <Text
                                className="flex-1 group-hover:underline"
                                type="body"
                                size="small"
                                style="bold"
                                color="slt-pepper.1000"
                            >
                                {item.name}
                            </Text>
                        </Link>
                    </SwiperSlide>
                ))}
            </Swiper>

            {showNavigation && (
                <>
                    <div className="swiper-button-next">
                        <GetIcon name="navigation-slide-arrow-right" size="md" />
                    </div>
                    <div className="swiper-button-prev">
                        <GetIcon name="navigation-slide-arrow-left" size="md" />
                    </div>
                </>
            )}
        </div>
    );
};

CategoryButtonTiles.defaultProps = {
    dataTestId: undefined,
};

export { CategoryButtonTiles };
